import React from "react";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useLoadMore from "hooks/useLoadMore";
import ButtonComponent from "components/button/ButtonComponent";
import SearchBox from "components/SearchBox";
import { formatDateTime } from "utils";
import useYhCompanyList from "../hooks/useYhCompanyList";
import CompanyTable from "../component/CompanyTable";
import CompanyDetailModal from "../component/CompanyDetailModal";
import { CompanyType } from "../types/company";

/**
 * YHCompanyList component displays a list of companies with pagination and filtering options.
 * It includes a search form, company filters, and a table to display company details.
 * @example
 * return (
 *   <YHCompanyList />
 * )
 * @returns {JSX.Element} The rendered component.
 * @remarks
 * - The component uses infinite scrolling to load more companies as the user scrolls down.
 * - It includes a modal to display detailed information about a selected company.
 * - The search form allows filtering companies by name and code.
 * - The component uses Material-UI for styling and layout.
 * @event handleDetailClick - Opens the company detail modal with the selected company's information.
 * @event handleCloseModal - Closes the company detail modal and clears the selected company.
 * @event handleCheckbox - Updates the state of the "Available only" checkbox.
 * @event handleScroll - Handles the infinite scrolling to load more companies.
 */
function YHCompanyList() {
  const navigate = useNavigate();
  const { companies, queryParams, loading, total, handleSearch, setQueryParams, onLoadMore } =
    useYhCompanyList();
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = React.useState<CompanyType | null>(null);
  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectCompany = (company: CompanyType): void => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box sx={{ display: { sm: "block", md: "flex" }, gap: "80px" }}>
          <Box>
            <Typography
              variant="h4"
              margin={0}
              textAlign="right"
              fontSize={{ xs: 24, md: 28, lg: 23 }}
              color="var(--gray-700)"
            >
              マスタ管理
            </Typography>
            <Typography
              variant="h5"
              margin={0}
              textAlign="right"
              fontSize={{ xs: 20, md: 28 }}
              color="var(--gray-700)"
            >
              企業
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                margin: "20px auto",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: "30px",
                  rowGap: "10px",
                }}
              >
                <SearchBox
                  title="企業名"
                  id="company_cd"
                  placeholder=""
                  sxLabel={{ width: "91px", minWidth: 0, textAlign: "center", paddingLeft: "10px" }}
                  sxTextField={{ width: "239px" }}
                  sx={{ width: "auto" }}
                  value={queryParams.companyName}
                  onChangeText={(value) => {
                    setQueryParams((pre) => ({ ...pre, companyName: value }));
                  }}
                />
                {/* <FormControlLabel
                  sx={{
                    fontSize: "23px",
                    color: "var(--black)",
                  }}
                  control={
                    <Checkbox
                      checked={queryParams.deleteFlag}
                      color="secondary"
                      value={0}
                      onChange={() => {
                        setQueryParams((pre) => ({
                          ...pre,
                          deleteFlag: !pre.deleteFlag,
                        }));
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "18px", color: "var(--black)" }}>
                      有効のみ
                    </Typography>
                  }
                /> */}
                {companies[0]?.updated_at && (
                  <Typography
                    sx={{
                      fontSize: { xs: 16, md: 21 },
                      color: "var(--gray-700)",
                      ml: { xs: 0, md: "30px" },
                    }}
                  >
                    最終連携日 {formatDateTime(companies[0]?.updated_at)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box textAlign="center" mb={2.5}>
          <ButtonComponent
            theme="dark"
            label="検索"
            onClick={handleSearch}
            sx={{
              backgroundColor: "var(--gray-500)",
              width: "190px",
              height: "50px",
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          px: { xs: 2, md: 2.5, lg: 5 },
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          py: 3,
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="end"
          fontSize={{ xs: "16px", md: "20px" }}
          sx={{ width: { xs: "100%", lg: "80%" } }}
        >
          件数【{total}件】
        </Box>
        <Box sx={{ overflowX: "auto", width: { xs: "100%", lg: "80%" } }}>
          <CompanyTable companies={companies} selectCompany={handleSelectCompany} />
          {renderNodeLoader()}
        </Box>
      </Box>
      {selectedCompany && (
        <CompanyDetailModal
          isOpen={isModalOpen}
          onClose={onCloseModal}
          selectedCompany={selectedCompany}
        />
      )}
    </>
  );
}

export default YHCompanyList;
